import { useMemo } from "react";
import { TemplateItem } from "../../store/Templates/templates.store";
import { TariffWithPricesViewType } from "../../store/Catalogs/types";
import { ExtendedArticleEntityType } from "../../store/Jobs/types";
import { ArticleTypeEnum } from "@/lib/supabase/supabaseEnums";
import { applyRounding, roundToCents } from "../../lib/utils/calculate";
import { RoundToType } from "../../store/Jobs/utils";

interface TemplateTotalsProps {
	templateItems: TemplateItem[];
	tariffsLookup: Record<string, Record<string, TariffWithPricesViewType>>;
	articlesLookup: Record<string, Record<string, ExtendedArticleEntityType>>;
	selectedPriceListId: string | null;
	tpValue: number;
	taxRate: number;
	roundTo: RoundToType;
}

interface TotalRow {
	name: string;
	value: string;
	bold?: boolean;
	borderBottom?: boolean;
}

export const TemplateTotals: React.FC<TemplateTotalsProps> = ({
	templateItems,
	tariffsLookup,
	articlesLookup,
	selectedPriceListId,
	tpValue,
	taxRate,
	roundTo,
}) => {
	const totals = useMemo(() => {
		let amountWork = 0;
		let totalTpValue = 0;
		let amountMaterial = 0;
		let amountPostage = 0;
		let amountExternalWork = 0;
		let amountFixedRate = 0;

		for (const item of templateItems) {
			if (item.tariffs_default_id || item.tariffs_custom_id) {
				const tariffId =
					item.tariffs_custom_id || item.tariffs_default_id;
				let tariff: TariffWithPricesViewType | null = null;

				for (const codeE in tariffsLookup) {
					if (tariffsLookup[codeE][tariffId || ""]) {
						tariff = tariffsLookup[codeE][tariffId || ""];
						break;
					}
				}

				if (tariff) {
					const priceItem = tariff.price_items?.find(
						(p) => p.price_list_id === selectedPriceListId
					);
					const price = priceItem?.price ?? 0;
					const tariffTpValue = tpValue ?? 0;
					const amount = price * item.quantity * tariffTpValue;
					amountWork += amount;
					totalTpValue += tariffTpValue * item.quantity;
				}
			} else if (item.article_id) {
				let article: ExtendedArticleEntityType | null = null;

				for (const codeE in articlesLookup) {
					for (const id in articlesLookup[codeE]) {
						if (articlesLookup[codeE][id].id === item.article_id) {
							article = articlesLookup[codeE][id];
							break;
						}
					}
					if (article) break;
				}

				if (article) {
					const amount = article.price * item.quantity;
					switch (article.type) {
						case ArticleTypeEnum.MATERIAL:
							amountMaterial += amount;
							break;
						case ArticleTypeEnum.POSTAGE:
							amountPostage += amount;
							break;
						case ArticleTypeEnum.EXTERNAL_WORK:
							amountExternalWork += amount;
							break;
						case ArticleTypeEnum.FIXED_RATE:
							amountFixedRate += amount;
							break;
					}
				}
			}
		}

		const subTotal = roundToCents(
			amountWork +
				amountFixedRate +
				amountMaterial +
				amountPostage +
				amountExternalWork
		);
		const mwst = roundToCents(subTotal * (taxRate / 100));
		const total = applyRounding(subTotal + mwst, roundTo);

		return {
			amountWork: amountWork + amountFixedRate,
			totalTpValue,
			amountMaterial,
			amountPostage,
			amountExternalWork,
			subTotal,
			mwst,
			total,
		};
	}, [
		templateItems,
		tariffsLookup,
		articlesLookup,
		selectedPriceListId,
		tpValue,
		taxRate,
		roundTo,
	]);

	const rows: TotalRow[] = [
		{ name: "Arbeit", value: totals.amountWork.toFixed(2), bold: true },
		{ name: "ArbeitTP", value: totals.totalTpValue.toFixed(2) },
		{
			name: "Material",
			value: totals.amountMaterial.toFixed(2),
			bold: true,
		},
		{ name: "Fremdarbeiten", value: totals.amountExternalWork.toFixed(2) },
		{
			name: "Porto",
			value: totals.amountPostage.toFixed(2),
			borderBottom: true,
		},
		{ name: "Subtotal", value: totals.subTotal.toFixed(2) },
		{ name: `MwSt. (${taxRate}%)`, value: totals.mwst.toFixed(2) },
		{ name: "Total", value: totals.total.toFixed(2), bold: true },
	];

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
			<table className="w-full">
				<tbody className="flex flex-col gap-1">
					{rows.map((row) => (
						<tr
							key={row.name}
							className={
								row.borderBottom
									? "border-b border-gray-200 pb-1"
									: ""
							}
						>
							<td
								className={`w-32 ${
									row.bold ? "font-bold" : ""
								}`}
							>
								{row.name}
							</td>
							<td
								className={`text-right ${
									row.bold ? "font-bold" : ""
								}`}
							>
								{row.value}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};
